
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SystemLog {
  id: string;
  action: string;
  status: string;
  description: string | null;
  metadata?: Record<string, any> | null;
  user_id?: string | null;
  created_at: string;
}

interface UseSystemLogsOptions {
  limit?: number;
  filter?: {
    action?: string;
    status?: string;
  };
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export function useSystemLogs(options: UseSystemLogsOptions = {}) {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const { limit = 20, filter = {}, autoRefresh = true, refreshInterval = 60000 } = options;

  const fetchLogs = async (): Promise<SystemLog[]> => {
    let query = (supabase
      .from('system_logs' as any)
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)) as any;
    
    if (filter.action) {
      query = query.eq('action', filter.action);
    }
    
    if (filter.status) {
      query = query.eq('status', filter.status);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching system logs:', error);
      throw error;
    }
    
    return data as unknown as SystemLog[];
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['systemLogs', limit, filter.action, filter.status],
    queryFn: fetchLogs,
    refetchInterval: autoRefresh ? refreshInterval : false,
  });

  useEffect(() => {
    if (data) {
      setLogs(data);
    }
  }, [data]);

  useEffect(() => {
    if (!autoRefresh) return;

    const channel = supabase
      .channel('system_logs_changes')
      .on('postgres_changes', 
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_logs',
        }, 
        (payload) => {
          setLogs(prevLogs => {
            const newLog = payload.new as SystemLog;
            if (prevLogs.some(log => log.id === newLog.id)) {
              return prevLogs;
            }
            return [newLog, ...prevLogs].slice(0, limit);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [autoRefresh, limit]);

  const createLog = async (action: string, status: string = 'info', description?: string, metadata?: Record<string, any>) => {
    try {
      // Get current user to set user_id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await (supabase
        .from('system_logs' as any)
        .insert({
          action,
          status,
          description,
          metadata: metadata || {},
          user_id: user.id
        })
        .select()
        .single()) as any;
        
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error creating log:', error);
      throw error;
    }
  };

  return {
    logs,
    isLoading,
    error,
    refetch,
    createLog
  };
}
